import io, {Socket} from "socket.io-client";
import msgPackParser from "socket.io-msgpack-parser";
import {API_VERSION, WS_URL} from "./http/env";

type EventsMap = {
  [event: string]: (...args: any[]) => void;
};

type AckError = {
  code: string;
  error: true;
  message: string;
};

type AckResponse<R> = R | AckError;

type AckCallback<R> = (res: AckResponse<R>) => void;

const rawSocket: Socket<EventsMap> = io(WS_URL, {
  parser: msgPackParser,
  query: {v: API_VERSION.toString()},
  transports: ["websocket", "polling"],
  autoConnect: false,
  forceNew: true,
  closeOnBeforeunload: true,
  rememberUpgrade: true,
});

const originalEmit = rawSocket.emit.bind(rawSocket);

type PatchedEmit = {
  <R = unknown>(ev: string, ack?: AckCallback<R>): Socket;
  <R = unknown>(
    ev: string,
    data?: object | string | number,
    ack?: AckCallback<R>,
  ): Socket;
};

export const patchedEmit: PatchedEmit = <R = unknown>(
  ev: string,
  data?: any,
  ack?: AckCallback<R>,
): Socket => {
  // Manually simulate arguments.length
  const argCount = [ev, data, ack].filter(v => v !== undefined).length;

  if (argCount > 3) {
    throw new Error("Invalid number of arguments passed to socket.emit");
  }

  // data is ack
  if (typeof data === "function") {
    return originalEmit(ev, undefined, data);
  }

  return originalEmit(ev, data, ack);
};


rawSocket.emit = patchedEmit as typeof patchedEmit;

//
// const originalEmitWithAck = rawSocket.emitWithAck.bind(rawSocket);
//
// function patchedEmitWithAck<R = unknown>(ev: string, data?: object | string | number): Promise<R> {
//   if (arguments.length > 2)
//     throw new Error("Invalid number of arguments passed to socket.emitWithAck");
//
//   return originalEmitWithAck(ev, data);
// }
//
// rawSocket.emitWithAck = patchedEmitWithAck as typeof patchedEmitWithAck;

/** Triggered by the server to request users to reload the page */
rawSocket.on("update", () => {
  rawSocket.disconnect();
  window.location.reload();
});

/** Socket io errors */
rawSocket.on("error", console.error);

type CustomSocket = Socket<EventsMap> & {
  emit: typeof patchedEmit;
};

const socket = rawSocket as CustomSocket;

export default socket;

rawSocket.on("connect_error", e => {
  // Handle a deprecated code version
  if (e.message === "OUTDATED_CLIENT") {
    socket.disconnect();
    window.location.reload();
  } else {
    console.error(e);
  }
});

// Disconnect and reconnect socket when user logs in or out
export const reconnectWebsocket = (brandId: number) => {
  socket.io.opts.query = {
    ...socket.io.opts.query,
    brand: brandId.toString(),
  };

  socket.disconnect();
  socket.connect();
};
